import { MovementPhase } from './movementData'
// Maintenance status types
export type MaintenanceStatus = 'green' | 'amber' | 'red' | 'black'
// Interface for daily maintenance status
export interface DailyMaintenanceStatus {
  day: number
  percentageFMC: number
  status: MaintenanceStatus
  notes: string
}
// Interface for maintenance nodes
export interface MaintenanceNode {
  id: string
  name: string
  label: string
  coordinates: [number, number] // [latitude, longitude]
  description: string
  capabilities: string[]
  isAlternate: boolean
}
// Interface for maintenance routes
export interface MaintenanceRoute {
  id: string
  name: string
  type: 'recovery' | 'mobile-repair'
  coordinates: [number, number][] // Array of coordinate pairs forming a route
  description: string
}
// Interface for NMC staging zones
export interface NMCStagingZone {
  id: string
  name: string
  coordinates: [number, number]
  description: string
  day: number
  vehiclesNMC: number
}
// Interface for field maintenance teams
export interface FieldMaintenanceTeam {
  id: string
  name: string
  coordinates: [number, number]
  description: string
  day: number
  capabilities: string[]
}
// Status colors for visual representation
export const maintenanceStatusColors: Record<MaintenanceStatus, string> = {
  green: '#059669', // Green
  amber: '#D97706', // Amber
  red: '#DC2626', // Red
  black: '#1F2937', // Black
}
// Get maintenance status based on percentage
export const getMaintenanceStatus = (percentage: number): MaintenanceStatus => {
  if (percentage >= 75) return 'green'
  if (percentage >= 50) return 'amber'
  if (percentage >= 25) return 'red'
  return 'black'
}
// Daily maintenance status data
export const dailyMaintenanceStatus: DailyMaintenanceStatus[] = [
  {
    day: 1,
    percentageFMC: 86,
    status: 'green',
    notes: 'Normal readiness – 3 MRAPs flagged for minor issues',
  },
  {
    day: 2,
    percentageFMC: 71,
    status: 'amber',
    notes: 'Early signs of degradation – tire and brake issues',
  },
  {
    day: 3,
    percentageFMC: 57,
    status: 'amber',
    notes: 'Increase in NMCs – expect 1–2 vehicles sidelined',
  },
  {
    day: 4,
    percentageFMC: 43,
    status: 'red',
    notes: 'Maintenance threshold reached – repair needed urgently',
  },
  {
    day: 5,
    percentageFMC: 100,
    status: 'green',
    notes: 'Recovery + refit at Fort Stockton',
  },
  {
    day: 6,
    percentageFMC: 86,
    status: 'green',
    notes: 'Expected minor repairs complete',
  },
  {
    day: 7,
    percentageFMC: 72,
    status: 'amber',
    notes: 'End-of-mission wear from terrain, usage',
  },
]
// Maintenance nodes data
export const maintenanceNodes: MaintenanceNode[] = [
  {
    id: 'fort-stockton',
    name: 'Fort Stockton Maintenance Node',
    label: 'M1',
    coordinates: [30.8941, -102.8793], // Fort Stockton
    description:
      'Recovery and major maintenance station. Full resupply on D+5.',
    capabilities: [
      'Major repairs',
      'Parts resupply',
      'Vehicle recovery',
      'Full maintenance teams',
    ],
    isAlternate: false,
  },
  {
    id: 'alpine',
    name: 'Alpine Maintenance Node',
    label: 'M1-ALT',
    coordinates: [30.3584, -103.6614], // Alpine
    description: 'Alternate node with field-level repair capacity.',
    capabilities: [
      'Field repairs',
      'Limited parts supply',
      'Basic recovery capability',
    ],
    isAlternate: true,
  },
]
// Maintenance routes data
export const maintenanceRoutes: MaintenanceRoute[] = [
  {
    id: 'recovery-route-1',
    name: 'Recovery Route R1',
    type: 'recovery',
    coordinates: [
      [29.3627, -100.8968], // Del Rio
      [30.1422, -102.3979], // Sanderson (intermediate point)
      [30.8941, -102.8793], // Fort Stockton
    ],
    description: 'Primary recovery route from Del Rio to Fort Stockton',
  },
  {
    id: 'mobile-repair-corridor-1',
    name: 'Mobile Repair Corridor MR1',
    type: 'mobile-repair',
    coordinates: [
      [29.5607, -104.3729], // Presidio
      [30.3094, -104.0177], // Marfa
      [30.8941, -102.8793], // Fort Stockton
      [30.1422, -102.3979], // Sanderson
      [29.3627, -100.8968], // Del Rio
      [28.7091, -100.4995], // Eagle Pass
      [27.5306, -99.4803], // Laredo
      [26.2033, -98.23], // McAllen
      [25.9017, -97.4975], // Brownsville
    ],
    description:
      'Mobile repair corridor along primary convoy axis for light/field maintenance',
  },
]
// NMC staging zones data
export const nmcStagingZones: NMCStagingZone[] = [
  {
    id: 'sanderson-nmc',
    name: 'Sanderson NMC Staging Zone',
    coordinates: [30.1422, -102.3979], // Sanderson
    description: 'Roadside pull-off near Sanderson for NMC vehicles',
    day: 3,
    vehiclesNMC: 2,
  },
  {
    id: 'del-rio-nmc',
    name: 'Del Rio NMC Staging Zone',
    coordinates: [29.3627, -100.8968], // Del Rio
    description: 'Staging area for NMC vehicles awaiting recovery',
    day: 4,
    vehiclesNMC: 5,
  },
  {
    id: 'eagle-pass-nmc',
    name: 'Eagle Pass NMC Staging Zone',
    coordinates: [28.7091, -100.4995], // Eagle Pass
    description: 'Temporary holding area for vehicles requiring maintenance',
    day: 6,
    vehiclesNMC: 3,
  },
]
// Field maintenance teams data
export const fieldMaintenanceTeams: FieldMaintenanceTeam[] = [
  {
    id: 'fmt-1',
    name: 'Field Maintenance Team Alpha',
    coordinates: [30.1422, -102.3979], // Sanderson
    description: 'Mobile maintenance team operating from tailgate vehicles',
    day: 2,
    capabilities: [
      'Tire replacement',
      'Basic fluid changes',
      'Minor electrical repairs',
    ],
  },
  {
    id: 'fmt-2',
    name: 'Field Maintenance Team Bravo',
    coordinates: [29.3627, -100.8968], // Del Rio
    description: 'Forward maintenance element with enhanced capabilities',
    day: 3,
    capabilities: [
      'Brake system repair',
      'Suspension work',
      'Limited drivetrain repair',
    ],
  },
  {
    id: 'fmt-3',
    name: 'Field Maintenance Team Charlie',
    coordinates: [27.5306, -99.4803], // Laredo
    description: 'Advanced field maintenance team with specialized equipment',
    day: 5,
    capabilities: [
      'Engine diagnostics',
      'Transmission repair',
      'Electrical system overhaul',
    ],
  },
]
// Helper function to convert time point to day number (reusing from other data files)
export const timePointToDay = (timePoint: string): number => {
  switch (timePoint) {
    case '24h':
      return 1
    case '48h':
      return 2
    case '72h':
      return 3
    case '96h':
      return 4
    case '5d':
      return 5
    case '6d':
      return 6
    case '7d':
      return 7
    default:
      return 0 // For "now", show day 0 (not started)
  }
}
// Helper function to get maintenance status for a specific day
export const getMaintenanceStatusForDay = (
  day: number,
): DailyMaintenanceStatus => {
  return (
    dailyMaintenanceStatus.find((status) => status.day === day) ||
    dailyMaintenanceStatus[0]
  )
}
